<script setup>
import { ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

// Sincroniza una nota de contacto recién creada con Zoho CRM. La selección del
// registro destino es AUTOMÁTICA: el más reciente cuyo estado no sea "Caída"
// (el backend devuelve la lista ordenada por fecha desc con un flag `fallen`).
// Se controla por ref desde el padre: openFor({ contact, content }).
const { t } = useI18n();
const store = useStore();

// idle → busy (searching/creating)
const stage = ref('idle');

const persistTarget = (contact, target) => {
  const existing = contact.custom_attributes || {};
  store.dispatch('contacts/update', {
    id: contact.id,
    customAttributes: { ...existing, crm_note_target: target },
  });
};

const createNote = async (contact, target, content) => {
  try {
    await store.dispatch('zohoCrmIntegration/createCrmNote', {
      module: target.module,
      recordId: target.record_id,
      content,
    });
    // eslint-disable-next-line no-console
    console.log('[crm-note-sync] note created on', target);
    useAlert(
      target.module === 'Deals'
        ? t('CRM_NOTE_SYNC.SUCCESS_SDI')
        : t('CRM_NOTE_SYNC.SUCCESS_LEAD')
    );
  } catch (e) {
    const msg = e?.response?.data?.error || e?.message || 'error';
    // eslint-disable-next-line no-console
    console.error('[crm-note-sync] create failed:', msg);
    useAlert(t('CRM_NOTE_SYNC.CREATE_ERROR', { error: msg }));
  }
};

// Punto de entrada llamado por el padre tras crear la nota en Chatwoot.
const openFor = async ({ contact, content }) => {
  if (!store.getters['zohoCrmIntegration/isConnected']) {
    // eslint-disable-next-line no-console
    console.log('[crm-note-sync] skipped, Zoho not connected');
    return;
  }

  stage.value = 'busy';
  try {
    // 1) Destino recordado → crear nota directa.
    const remembered = contact.custom_attributes?.crm_note_target;
    if (remembered?.module && remembered?.record_id) {
      // eslint-disable-next-line no-console
      console.log('[crm-note-sync] using remembered target', remembered);
      await createNote(contact, remembered, content);
      return;
    }

    // 2) Buscar y elegir automáticamente el más reciente no-caído.
    const found = await store.dispatch('zohoCrmIntegration/searchRecords', {
      phone: contact.phone_number || '',
      email: contact.email || '',
    });
    // eslint-disable-next-line no-console
    console.log('[crm-note-sync] search →', found.length, 'records');
    const best = found.find(r => !r.fallen) || found[0];
    if (!best) {
      useAlert(t('CRM_NOTE_SYNC.NOT_FOUND'));
      return;
    }
    // eslint-disable-next-line no-console
    console.log(
      '[crm-note-sync] auto-selected',
      best.module,
      best.id,
      'state=',
      best.state
    );
    const target = { module: best.module, record_id: best.id };
    persistTarget(contact, target);
    await createNote(contact, target, content);
  } catch (e) {
    const msg = e?.response?.data?.error || e?.message || 'error';
    // eslint-disable-next-line no-console
    console.error('[crm-note-sync] search failed:', msg);
    useAlert(t('CRM_NOTE_SYNC.SEARCH_ERROR', { error: msg }));
  } finally {
    stage.value = 'idle';
  }
};

defineExpose({ openFor });
</script>

<template>
  <!-- Indicador discreto mientras sincroniza (sin selector: es automático). -->
  <Teleport to="body">
    <div
      v-if="stage === 'busy'"
      class="fixed bottom-4 ltr:right-4 rtl:left-4 z-50 flex items-center gap-2 rounded-lg bg-n-background border border-n-weak shadow-lg px-3 py-2 text-sm text-n-slate-11"
    >
      <span class="i-lucide-loader-2 size-4 animate-spin" />
      <span>{{ t('CRM_NOTE_SYNC.SYNCING') }}</span>
    </div>
  </Teleport>
</template>
